import { Button, Dialog, TextInput, Tooltip, Typography } from '@neo4j-ndl/react';
import { APP_SCOPE } from '@nx/constants';
import { createLogger } from '@nx/logger/legacy';
import type { AuraDatabase } from '@nx/state';
import { consoleApi, getApiError, getErrorMessage, useNotifications } from '@nx/state';
import { isNotNullish } from '@nx/stdlib';
import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useEffect, useState } from 'react';
import type { SyntheticEvent } from 'react';

const logger = createLogger(APP_SCOPE.framework);

type Props = {
  onClose: () => void;
  onConfirm: () => void;
  database: AuraDatabase;
};

export const DeleteDatabaseModal = (props: Props) => {
  const { onClose, onConfirm, database } = props;

  const handleClose = () => {
    onClose();
  };

  const [tipOpen, setTipOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const handleConfirmTextChange = (value: string) => {
    setConfirmText(value);
  };

  useEffect(() => {
    if (tipOpen) {
      setTimeout(() => setTipOpen(false), 2000);
    }
  }, [tipOpen]);

  const [deleteDatabase] = consoleApi.useDeleteDatabaseMutation();

  const { addNotification } = useNotifications();

  const handleDelete = (event: SyntheticEvent) => {
    event.preventDefault();
    deleteDatabase({ instanceId: database.instanceId, id: database.id })
      .unwrap()
      .catch((e: FetchBaseQueryError | SerializedError) => {
        const error = getApiError(e);
        if (isNotNullish(error.message)) {
          logger.error(error.message);
        }
        addNotification({
          type: 'danger',
          title: `Failed to delete '${database.name}'`,
          description: getErrorMessage(error),
          timeout: 5000,
        });
      });
    // deliberately not awaiting
    onConfirm();
  };

  return (
    <Dialog isOpen onClose={handleClose} size="small">
      <Dialog.Header className="flex flex-col gap-1">
        <div>
          Delete{' '}
          <Tooltip isOpen={tipOpen} type="simple" placement="right" isPortaled={false}>
            <Tooltip.Trigger
              className="cursor-text"
              htmlAttributes={{
                onClick: () => {
                  void navigator.clipboard.writeText(database.name);
                  if (!tipOpen) {
                    setTipOpen(true);
                  }
                },
                'aria-label': 'Copy text',
              }}
            >
              <span className="font-normal">{database.name}</span>
            </Tooltip.Trigger>
            <Tooltip.Content className="flex items-center">Copied to clipboard</Tooltip.Content>
          </Tooltip>{' '}
        </div>
        <Dialog.Description>
          This action is irreversible. Deleting a database will remove all data and cannot be undone.
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.Content>
        <form onSubmit={handleDelete} id="delete-database-form" className="flex flex-col gap-6">
          <TextInput
            value={confirmText}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleConfirmTextChange(e.target.value)}
            label={
              <Typography variant="subheading-medium" className="text-neutral-text-default">
                Type the name of the database below to confirm
              </Typography>
            }
            htmlAttributes={{
              autoFocus: true,
              placeholder: database.name,
            }}
            isFluid
          />
        </form>
      </Dialog.Content>
      <Dialog.Actions>
        <Button color="neutral" fill="outlined" onClick={handleClose} isDisabled={false}>
          Cancel
        </Button>
        <Button
          color="danger"
          type="submit"
          isDisabled={confirmText !== database.name}
          htmlAttributes={{
            form: 'delete-database-form',
          }}
        >
          Delete
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};
